/**
 * Settings Sync Service
 *
 * Manages real-time settings synchronization via SSE.
 * - Connects to server when authenticated
 * - Receives settings update notifications
 * - Checks version on startup and syncs if different
 * - Server always wins on conflicts
 */

import { apiBase } from "./api-config";
import { getDb } from "../db";
import type { UserSettings } from "../types";

export interface SettingsSyncState {
  connected: boolean;
  version: number;
  lastUpdate?: Date;
}

export interface SettingsChangedEvent {
  version: number;
  data: UserSettings;
  dataChanged: boolean; // true if actual data differs, not just version
}

type SettingsListener = (event: SettingsChangedEvent) => void;

class SettingsSyncService {
  private _eventSource: EventSource | null = null;
  private _listeners: Set<SettingsListener> = new Set();
  private _state: SettingsSyncState = {
    connected: false,
    version: 0,
  };
  private _reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private _reconnectAttempts = 0;
  private _maxReconnectAttempts = 5;
  private _isAuthenticated = false;

  /**
   * Start sync service - call when user authenticates
   */
  start(): void {
    if (this._isAuthenticated) return;
    this._isAuthenticated = true;
    this._reconnectAttempts = 0;
    this._connect();
  }

  /**
   * Stop sync service - call on logout
   */
  stop(): void {
    this._isAuthenticated = false;
    this._disconnect();
    if (this._reconnectTimeout) {
      clearTimeout(this._reconnectTimeout);
      this._reconnectTimeout = null;
    }
  }

  /**
   * Subscribe to settings change events
   */
  subscribe(listener: SettingsListener): () => void {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  /**
   * Get current sync state
   */
  get state(): SettingsSyncState {
    return { ...this._state };
  }

  /**
   * Check server settings and sync if version differs.
   * Returns true if settings were updated from server.
   */
  async checkAndSync(): Promise<{ updated: boolean; dataChanged: boolean }> {
    try {
      const { fetchSettings } = await import("./settings-api");
      const serverResult = await fetchSettings<UserSettings>();

      const db = getDb();
      const local = await db.settings.get("settings");
      const localVersion = local?._serverVersion ?? 0;

      if (serverResult.version === localVersion) {
        console.log("[SettingsSync] Version matches, no sync needed");
        return { updated: false, dataChanged: false };
      }

      // Version differs - check if data actually changed
      const dataChanged = !this._settingsEqual(
        local as UserSettings | undefined,
        serverResult.data ?? undefined,
      );

      if (serverResult.data) {
        // Server wins - update local
        await db.settings.put({
          ...serverResult.data,
          id: "settings",
          _syncStatus: "synced",
          _serverVersion: serverResult.version,
        });

        this._state.version = serverResult.version;
        this._state.lastUpdate = new Date();

        console.log(
          `[SettingsSync] Synced from server: v${localVersion} -> v${serverResult.version}, dataChanged=${dataChanged}`,
        );

        // Notify listeners if data actually changed
        if (dataChanged) {
          this._notifyListeners({
            version: serverResult.version,
            data: serverResult.data,
            dataChanged: true,
          });
        }

        return { updated: true, dataChanged };
      }

      return { updated: false, dataChanged: false };
    } catch (error) {
      console.warn("[SettingsSync] Check failed:", error);
      return { updated: false, dataChanged: false };
    }
  }

  /**
   * Compare two settings objects for equality (ignoring sync metadata)
   */
  private _settingsEqual(
    a: UserSettings | undefined,
    b: UserSettings | undefined,
  ): boolean {
    if (!a && !b) return true;
    if (!a || !b) return false;

    // Create clean copies without sync metadata
    const cleanA = this._cleanSettings(a);
    const cleanB = this._cleanSettings(b);

    return JSON.stringify(cleanA) === JSON.stringify(cleanB);
  }

  /**
   * Remove sync metadata from settings for comparison
   */
  private _cleanSettings(settings: UserSettings): Partial<UserSettings> {
    const copy = { ...settings } as Record<string, unknown>;
    delete copy.id;
    delete copy._syncStatus;
    delete copy._serverVersion;
    delete copy._localVersion;
    delete copy._conflictData;
    return copy as Partial<UserSettings>;
  }

  private _connect(): void {
    if (!this._isAuthenticated || this._eventSource) return;

    console.log("[SettingsSync] Connecting to SSE...");

    try {
      this._eventSource = new EventSource(`${apiBase}/api/settings/stream`, {
        withCredentials: true,
      });

      this._eventSource.onopen = () => {
        console.log("[SettingsSync] SSE connected");
        this._state.connected = true;
        this._reconnectAttempts = 0;
      };

      this._eventSource.addEventListener("connected", (event) => {
        const data = JSON.parse(event.data);
        console.log(
          `[SettingsSync] Server reports version: ${data.version}, clientId: ${data.clientId}`,
        );
        this._state.version = data.version;

        // Check if we need to sync on connect
        this.checkAndSync();
      });

      this._eventSource.addEventListener("settings-updated", async (event) => {
        const data = JSON.parse(event.data);
        console.log(
          `[SettingsSync] Received settings update, version: ${data.version}`,
        );

        // Get local settings to compare
        const db = getDb();
        const local = await db.settings.get("settings");

        // Skip if we already have this version (self-echo from our own save)
        if (local?._serverVersion === data.version) {
          console.log(
            `[SettingsSync] Skipping self-echo (already at version ${data.version})`,
          );
          return;
        }

        const dataChanged = !this._settingsEqual(
          local as UserSettings | undefined,
          data.data as UserSettings | undefined,
        );

        // Server always wins - update local
        if (data.data) {
          await db.settings.put({
            ...data.data,
            id: "settings",
            _syncStatus: "synced",
            _serverVersion: data.version,
          });
        }

        this._state.version = data.version;
        this._state.lastUpdate = new Date();

        // Only notify if data actually changed
        if (dataChanged) {
          this._notifyListeners({
            version: data.version,
            data: data.data,
            dataChanged: true,
          });
        }
      });

      this._eventSource.addEventListener("heartbeat", () => {
        // Just keep-alive, no action needed
      });

      this._eventSource.onerror = (error) => {
        console.warn("[SettingsSync] SSE error:", error);
        this._state.connected = false;
        this._handleDisconnect();
      };
    } catch (error) {
      console.error("[SettingsSync] Failed to create EventSource:", error);
      this._handleDisconnect();
    }
  }

  private _disconnect(): void {
    if (this._eventSource) {
      this._eventSource.close();
      this._eventSource = null;
    }
    this._state.connected = false;
  }

  private _handleDisconnect(): void {
    this._disconnect();

    if (!this._isAuthenticated) return;

    // Exponential backoff for reconnection
    this._reconnectAttempts++;
    if (this._reconnectAttempts <= this._maxReconnectAttempts) {
      const delay = Math.min(
        1000 * Math.pow(2, this._reconnectAttempts - 1),
        30000,
      );
      console.log(
        `[SettingsSync] Reconnecting in ${delay}ms (attempt ${this._reconnectAttempts}/${this._maxReconnectAttempts})`,
      );
      this._reconnectTimeout = setTimeout(() => {
        this._reconnectTimeout = null;
        this._connect();
      }, delay);
    } else {
      console.warn("[SettingsSync] Max reconnect attempts reached");
    }
  }

  private _notifyListeners(event: SettingsChangedEvent): void {
    for (const listener of this._listeners) {
      try {
        listener(event);
      } catch (error) {
        console.error("[SettingsSync] Listener error:", error);
      }
    }
  }
}

export const settingsSyncService = new SettingsSyncService();
